(function (globalScope) {
  'use strict';

  class WqPredictiveSearchController {
    constructor(root, options = {}) {
      this.root = root;
      this.document = options.document || root.ownerDocument;
      this.input = root.querySelector('[data-wq-search-input]');
      this.results = root.querySelector('[data-wq-search-results]');
      this.content = root.querySelector('[data-wq-search-content]');
      this.status = root.querySelector('[data-wq-search-status]');
      this.form = root.querySelector('form');
      this.closeButton = root.querySelector('[data-wq-search-close]');
      this.screen = root.querySelector('[data-wq-search-screen]');
      this.fetch = options.fetch || globalScope.fetch.bind(globalScope);
      this.parseResponse = options.parseResponse || this.defaultParseResponse.bind(this);
      this.getOptions = options.getOptions || (() => Array.from(this.content.querySelectorAll('a[href]')));
      this.navigate = options.navigate || ((url) => globalScope.location.assign(url));
      this.debounceMs = options.debounceMs ?? 250;
      this.route = root.getAttribute('data-route');
      this.loadingText = root.getAttribute('data-loading-text');
      this.emptyText = root.getAttribute('data-empty-text');
      this.errorText = root.getAttribute('data-error-text');
      this.state = 'idle';
      this.activeIndex = -1;
      this.requestSequence = 0;
      this.abortController = null;
      this.debounceTimer = null;
      this.opener = null;
      this.bindEvents();
      this.setState('idle');
    }

    bindEvents() {
      this.input.addEventListener('input', () => this.onInput());
      this.input.addEventListener('keydown', (event) => this.onKeydown(event));
      this.closeButton.addEventListener('click', (event) => {
        event.preventDefault();
        this.close({ restoreFocus: true });
      });
      this.screen.addEventListener('click', () => this.close({ restoreFocus: true }));
      this.content.addEventListener('mouseover', (event) => {
        const target = event.target && event.target.closest ? event.target.closest('[data-wq-search-option]') : null;
        if (!target) return;
        const index = this.options().indexOf(target);
        if (index >= 0) this.selectIndex(index);
      });

      if (this.document) {
        this.document.addEventListener('predictive-search:open', (event) => {
          if (!event.detail || event.detail.context !== 'header') return;
          this.opener = this.opener || this.document.activeElement;
          this.open();
          globalScope.setTimeout(() => this.input.focus(), 100);
        });
        this.document.addEventListener('predictive-search:close', () => this.close({ restoreFocus: true }));
        this.document.addEventListener('keydown', (event) => {
          if (event.key !== 'Escape' || !this.root.classList.contains('is-active')) return;
          event.preventDefault();
          this.close({ restoreFocus: true });
        });
      }

      const header = this.root.closest ? this.root.closest('[data-wq-header]') : null;
      const openButton = header && header.querySelector ? header.querySelector('.js-search-header') : null;
      if (openButton) openButton.addEventListener('click', (event) => {
        event.preventDefault();
        this.opener = openButton;
        this.open();
        globalScope.setTimeout(() => this.input.focus(), 100);
      }, true);
    }

    onInput() {
      const term = this.input.value.trim();
      globalScope.clearTimeout(this.debounceTimer);
      this.cancelRequest();
      const sequence = ++this.requestSequence;
      this.clearSelection();

      if (!term) {
        this.content.innerHTML = '';
        this.results.hidden = true;
        this.setState('idle');
        return;
      }

      this.open();
      this.setState('debouncing');
      this.debounceTimer = globalScope.setTimeout(() => this.request(term, sequence), this.debounceMs);
    }

    async request(term, sequence) {
      this.abortController = new globalScope.AbortController();
      this.setState('loading');
      const productParams = new URLSearchParams({
        q: `${term} vendor:WeQueen`,
        'resources[type]': 'product',
        'resources[limit]': '6',
        section_id: 'search-results'
      });
      const contentParams = new URLSearchParams({
        q: term,
        'resources[type]': 'page,article',
        'resources[limit]': '3',
        'resources[limit_scope]': 'each',
        section_id: 'search-results'
      });

      try {
        const responses = await Promise.all([productParams, contentParams].map((params) => this.fetch(`${this.route}?${params.toString()}`, {
          signal: this.abortController.signal,
          headers: { Accept: 'text/html' }
        })));
        if (responses.some((response) => !response.ok)) throw new Error(`Predictive search HTTP ${responses.find((response) => !response.ok)?.status || 'error'}`);
        const texts = await Promise.all(responses.map((response) => response.text()));
        if (sequence !== this.requestSequence) return;
        const parsed = texts.map((text) => this.parseResponse(text));
        const merged = this.mergeResponses(parsed);
        this.content.innerHTML = merged.html;
        this.decorateOptions();
        this.setState(merged.empty ? 'empty' : 'results');
      } catch (error) {
        if (error && error.name === 'AbortError') return;
        if (sequence !== this.requestSequence) return;
        this.setState('error');
      } finally {
        if (sequence === this.requestSequence) {
          this.results.setAttribute('aria-busy', 'false');
          this.abortController = null;
        }
      }
    }

    defaultParseResponse(text) {
      const parsed = new globalScope.DOMParser().parseFromString(text, 'text/html');
      const section = parsed.querySelector('#shopify-section-search-results');
      if (!section) throw new Error('Predictive search section missing');
      const resultSet = section.querySelector('.predictive-search-results');
      return { html: resultSet?.innerHTML || '', empty: !resultSet || resultSet.classList.contains('predictive-search-results--none'), fragment: true };
    }

    mergeResponses(responses) {
      const unique = [...new Set(responses.filter((response) => response && !response.empty && response.html).map((response) => response.html))];
      const html = unique.join('');
      if (!responses.some((response) => response?.fragment)) return { html, empty: !html };
      return { html: `<div class="predictive-search-results" id="predictive-search-results">${html}</div>`, empty: !html };
    }

    decorateOptions() {
      this.clearSelection();
      this.options().forEach((item, index) => {
        item.id = `WqPredictiveSearchOption-${index + 1}`;
        item.setAttribute('role', 'option');
        item.setAttribute('aria-selected', 'false');
        item.setAttribute('data-wq-search-option', '');
      });
    }

    options() { return Array.from(this.getOptions()); }

    onKeydown(event) {
      const items = this.options();
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close({ restoreFocus: true });
        return;
      }
      if (event.key === 'Enter') {
        if (this.activeIndex < 0 || !items[this.activeIndex]) return;
        event.preventDefault();
        this.navigate(items[this.activeIndex].href);
        return;
      }
      if (!items.length || !['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      if (event.key === 'Home') this.selectIndex(0);
      if (event.key === 'End') this.selectIndex(items.length - 1);
      if (event.key === 'ArrowDown') this.selectIndex(this.activeIndex < items.length - 1 ? this.activeIndex + 1 : 0);
      if (event.key === 'ArrowUp') this.selectIndex(this.activeIndex > 0 ? this.activeIndex - 1 : items.length - 1);
    }

    selectIndex(index) {
      const items = this.options();
      if (!items[index]) return;
      items.forEach((item, itemIndex) => {
        const active = itemIndex === index;
        if (item.classList.toggle) item.classList.toggle('is-active', active);
        else if (active) item.classList.add('is-active');
        else item.classList.remove('is-active');
        item.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      this.activeIndex = index;
      this.input.setAttribute('aria-activedescendant', items[index].id);
    }

    clearSelection() {
      this.options().forEach((item) => {
        item.classList.remove('is-active');
        item.setAttribute('aria-selected', 'false');
      });
      this.activeIndex = -1;
      this.input.removeAttribute('aria-activedescendant');
    }

    setState(state) {
      this.state = state;
      this.root.setAttribute('data-state', state);
      const message = state === 'loading' ? this.loadingText : state === 'empty' ? this.emptyText : state === 'error' ? this.errorText : '';
      this.status.textContent = message || '';
      this.status.hidden = !message;
      this.content.hidden = ['idle', 'debouncing', 'loading', 'error', 'closed'].includes(state);
      this.results.hidden = ['idle', 'closed'].includes(state);
      this.results.style.display = this.results.hidden ? 'none' : 'block';
      this.results.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');
    }

    open() {
      this.root.classList.add('is-active');
      this.screen.style.display = 'block';
      this.closeButton.style.display = 'flex';
      this.input.setAttribute('aria-expanded', 'true');
      if (this.document && this.document.body) this.document.body.classList.add('predictive-overflow-hidden');
      if (this.state === 'closed') this.setState('idle');
    }

    close({ restoreFocus = true } = {}) {
      globalScope.clearTimeout(this.debounceTimer);
      this.cancelRequest();
      this.requestSequence += 1;
      this.clearSelection();
      this.root.classList.remove('is-active');
      this.screen.style.display = 'none';
      this.closeButton.style.display = 'none';
      this.input.setAttribute('aria-expanded', 'false');
      if (this.document && this.document.body) this.document.body.classList.remove('predictive-overflow-hidden');
      this.setState('closed');
      if (this.document && typeof CustomEvent !== 'undefined') this.document.dispatchEvent(new CustomEvent('predictive-search:close-all'));
      if (restoreFocus && this.opener && typeof this.opener.focus === 'function') this.opener.focus();
    }

    cancelRequest() {
      if (!this.abortController) return;
      this.abortController.abort();
      this.abortController = null;
    }
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { WqPredictiveSearchController };
  if (globalScope.customElements && !globalScope.customElements.get('wq-predictive-search')) {
    class WqPredictiveSearchElement extends HTMLElement {
      connectedCallback() { if (!this.controller) this.controller = new WqPredictiveSearchController(this); }
    }
    globalScope.customElements.define('wq-predictive-search', WqPredictiveSearchElement);
  }
})(typeof window !== 'undefined' ? window : globalThis);
